// Batch units that count PORTIONS rather than weigh them.
//
// A recipe written "for 50 units" (the chia puddings, the overnight oats)
// scales on how many portions you need — 33 portions is 33/50 of the batch.
// Scaling it against a POUND figure divides pounds by a portion count, which
// is a category error: 33 meals x 0.375 lbs = 12.375 reads as a quarter batch
// when the answer is two thirds.
//
// Mirrors COUNT_BATCH_UNITS in server/routers/helpers.py — keep the two in sync.
export const COUNT_BATCH_UNITS = new Set(['each', 'ea', 'unit', 'units', 'portion', 'portions'])

export const isCountBatch = (unit) =>
  COUNT_BATCH_UNITS.has(String(unit || '').trim().toLowerCase())

/**
 * What to scale a recipe to.
 *
 * @param {string} baseBatchUnit  the recipe's own batch unit
 * @param {number} portionCount   how many portions/meals are needed
 * @param {number} lbsNeeded      how many pounds are needed
 * @returns {number|null} the target, or null when neither figure is usable
 */
export function scaleTarget(baseBatchUnit, portionCount, lbsNeeded) {
  if (isCountBatch(baseBatchUnit)) {
    if (portionCount > 0) return portionCount
    return null  // never fall back to pounds here — that's the bug this prevents
  }
  return lbsNeeded > 0 ? lbsNeeded : null
}

// Main ordering groups that represent one produced menu component. Additional
// ingredients are inputs to these recipes and must never become independent
// recipe scaling targets.
export const RECIPE_SCALE_GROUPS = ['proteins', 'vegetables', 'carbs', 'sauces', 'bases', 'wellness_shots']

/**
 * Build one current-menu production target per stable recipe DB id.
 *
 * Ordering rows may merge multiple component names into one purchase line, and
 * their historical recipe_json_id slugs can differ from the recipe library.
 * The nested component entries retain the stable numeric recipe_id and their
 * own counts/weights, so use those instead of the purchase-line slug.
 */
export function buildRecipeScaleTargets(orderingData) {
  const targets = new Map()
  const rows = RECIPE_SCALE_GROUPS.flatMap((group) => orderingData?.[group] || [])
  // A menu component can land under Additional when its ingredient/category
  // metadata is imperfect (Almond Coconut Granola is a current example). Only
  // include Additional rows that still carry nested menu-component detail;
  // ordinary recipe input rows use source_recipes instead and are excluded.
  for (const additionalGroup of Object.values(orderingData?.additional || {})) {
    for (const row of additionalGroup || []) {
      if (Array.isArray(row.components) && row.components.length) rows.push(row)
    }
  }

  for (const row of rows) {
    const rawNeeded = Number(row.needed_raw ?? row.needed) || 0
    const withBuffer = Number(row.needed_with_buffer) || 0
    const bufferFactor = rawNeeded > 0 && withBuffer > 0 ? withBuffer / rawNeeded : 1
    const components = Array.isArray(row.components) ? row.components : []

    if (components.length) {
      for (const component of components) {
        if (component.recipe_id == null) continue
        const key = String(component.recipe_id)
        const target = targets.get(key) || { recipeId: component.recipe_id, portionCount: 0, lbsNeeded: 0 }
        target.portionCount += Number(component.count) || 0
        target.lbsNeeded += (Number(component.needed_lbs) || 0) * bufferFactor
        targets.set(key, target)
      }
      continue
    }

    // Defensive fallback for older/local calculations without nested
    // component detail. Numeric DB ids still take priority over mutable slugs.
    if (row.recipe_id != null) {
      const key = String(row.recipe_id)
      const target = targets.get(key) || { recipeId: row.recipe_id, portionCount: 0, lbsNeeded: 0 }
      target.portionCount += Number(row.total_meal_count) || 0
      target.lbsNeeded += withBuffer || rawNeeded
      targets.set(key, target)
    }
  }

  return targets
}

export function menuScaleTargetForRecipe(recipe, orderingData) {
  const dbId = recipe?.db_id ?? recipe?.id
  const target = buildRecipeScaleTargets(orderingData).get(String(dbId))
  if (!target) return null
  return scaleTarget(recipe.base_batch_unit, target.portionCount, target.lbsNeeded)
}
